import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { FaCoins, FaShoppingCart, FaChartBar, FaHistory, FaTag, FaCheck, FaExclamationTriangle, FaTimes, FaSync, FaPaypal } from 'react-icons/fa';

const API_URL = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// Dark theme styles matching the project
const styles = {
    pageWrapper: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: '#0f1419',
        padding: '30px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden'
    },
    container: {
        width: '100%',
        maxWidth: '1200px',
        color: '#FFFFFF',
        paddingBottom: '50px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid rgba(100, 150, 200, 0.3)'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    headerIcon: {
        fontSize: '32px',
        color: '#64748B'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#FFFFFF',
        margin: 0
    },
    subtitle: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)',
        margin: '4px 0 0 0'
    },
    refreshBtn: {
        background: 'rgba(100, 116, 139, 0.2)',
        border: '1px solid rgba(100, 116, 139, 0.4)',
        borderRadius: '8px',
        padding: '10px',
        cursor: 'pointer',
        color: '#94A3B8',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    balanceCard: {
        background: 'linear-gradient(135deg, #334155 0%, #475569 50%, #64748B 100%)',
        borderRadius: '16px',
        padding: '30px',
        marginBottom: '30px',
        boxShadow: '0 8px 32px rgba(71, 85, 105, 0.4)'
    },
    balanceGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '30px',
        alignItems: 'center'
    },
    balanceMain: {
        color: 'white'
    },
    balanceLabel: {
        fontSize: '14px',
        opacity: 0.8,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '8px'
    },
    balanceValue: {
        fontSize: '48px',
        fontWeight: '700',
        marginBottom: '16px'
    },
    progressBar: {
        height: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '8px'
    },
    progressFill: {
        height: '100%',
        backgroundColor: 'white',
        borderRadius: '6px',
        transition: 'width 0.3s ease'
    },
    balanceStats: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
    },
    statBox: {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center'
    },
    statValue: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'white'
    },
    statLabel: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)',
        marginTop: '4px'
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        marginTop: '12px'
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid rgba(100, 150, 200, 0.2)',
        paddingBottom: '16px'
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s ease'
    },
    tabActive: {
        backgroundColor: '#475569',
        color: 'white'
    },
    tabInactive: {
        backgroundColor: 'rgba(35, 40, 50, 0.9)',
        color: 'rgba(255, 255, 255, 0.7)'
    },
    promoBanner: {
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        border: '1px solid rgba(34, 197, 94, 0.4)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    promoIcon: {
        color: '#22c55e',
        fontSize: '20px'
    },
    promoText: {
        color: '#22c55e',
        fontSize: '14px'
    },
    packagesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px'
    },
    packageCard: {
        backgroundColor: 'rgba(35, 40, 50, 0.9)',
        border: '1px solid rgba(100, 120, 150, 0.25)',
        borderRadius: '16px',
        padding: '24px',
        position: 'relative',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
    },
    packageCardPopular: {
        border: '2px solid #64748B',
        boxShadow: '0 0 20px rgba(100, 116, 139, 0.3)'
    },
    popularBadge: {
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#475569',
        color: 'white',
        padding: '4px 16px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
    },
    packageName: {
        fontSize: '24px',
        fontWeight: '700',
        color: 'white',
        marginBottom: '8px'
    },
    packageDescription: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)',
        marginBottom: '20px'
    },
    packagePrice: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#22c55e',
        marginBottom: '4px'
    },
    packageTokens: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: '8px'
    },
    bonusBadge: {
        display: 'inline-block',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        color: '#22c55e',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        marginBottom: '16px'
    },
    featureList: {
        listStyle: 'none',
        padding: 0,
        margin: '16px 0'
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '10px',
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.8)'
    },
    featureIcon: {
        color: '#22c55e',
        fontSize: '14px'
    },
    selectBtn: {
        width: '100%',
        padding: '14px',
        borderRadius: '10px',
        border: 'none',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    selectBtnPrimary: {
        backgroundColor: '#475569',
        color: 'white'
    },
    selectBtnSecondary: {
        backgroundColor: 'rgba(100, 116, 139, 0.2)',
        color: '#94A3B8',
        border: '1px solid rgba(100, 116, 139, 0.4)'
    },
    card: {
        backgroundColor: 'rgba(35, 40, 50, 0.9)',
        border: '1px solid rgba(100, 120, 150, 0.25)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px'
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: 'white',
        marginBottom: '16px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        textAlign: 'left',
        padding: '12px',
        borderBottom: '1px solid rgba(100, 150, 200, 0.2)',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    td: {
        padding: '12px',
        borderBottom: '1px solid rgba(100, 150, 200, 0.1)',
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '14px'
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modalContent: {
        backgroundColor: '#1a1f26',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '480px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid rgba(100, 150, 200, 0.3)'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: 'white'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '20px',
        cursor: 'pointer'
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        borderRadius: '10px',
        border: '1px solid rgba(100, 150, 200, 0.3)',
        backgroundColor: 'rgba(35, 40, 50, 0.9)',
        color: 'white',
        fontSize: '14px',
        marginBottom: '16px',
        boxSizing: 'border-box'
    },
    summaryBox: {
        backgroundColor: 'rgba(100, 116, 139, 0.15)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '12px',
        fontSize: '14px'
    },
    summaryLabel: {
        color: 'rgba(255, 255, 255, 0.6)'
    },
    summaryValue: {
        color: 'white',
        fontWeight: '500'
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '12px',
        borderTop: '1px solid rgba(100, 150, 200, 0.2)',
        fontSize: '18px',
        fontWeight: '600'
    },
    alert: {
        padding: '12px 16px',
        borderRadius: '8px',
        marginBottom: '16px',
        fontSize: '14px'
    },
    alertSuccess: {
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        border: '1px solid rgba(34, 197, 94, 0.4)',
        color: '#22c55e'
    },
    alertError: {
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        border: '1px solid rgba(239, 68, 68, 0.4)',
        color: '#ef4444'
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: 'rgba(255, 255, 255, 0.6)'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: 'rgba(255, 255, 255, 0.5)'
    }
};

const TokenSettings = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(null);
    const [packages, setPackages] = useState([]);
    const [usageSummary, setUsageSummary] = useState(null);
    const [purchaseHistory, setPurchaseHistory] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [promoCode, setPromoCode] = useState('');
    const [promoValidation, setPromoValidation] = useState(null);
    const [validatingPromo, setValidatingPromo] = useState(false);
    const [purchaseDialog, setPurchaseDialog] = useState({ open: false, package: null });
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseResult, setPurchaseResult] = useState(null);
    const [paypalSuccess, setPaypalSuccess] = useState(null);
    const [processingPaypal, setProcessingPaypal] = useState(false);
    // Debug state
    const [debugValue, setDebugValue] = useState('');
    const [debugLoading, setDebugLoading] = useState(false);
    const [debugMessage, setDebugMessage] = useState(null);

    const fetchTokenData = useCallback(async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            const [balanceRes, packagesRes, usageRes, historyRes] = await Promise.all([
                fetch(`${API_URL}/tokens/balance`, { headers }),
                fetch(`${API_URL}/tokens/packages`),
                fetch(`${API_URL}/tokens/usage/summary?period=month`, { headers }),
                fetch(`${API_URL}/tokens/purchases`, { headers })
            ]);

            if (balanceRes.ok) {
                const data = await balanceRes.json();
                setBalance(data.data);
            }
            if (packagesRes.ok) {
                const data = await packagesRes.json();
                setPackages(data.data || []);
            }
            if (usageRes.ok) {
                const data = await usageRes.json();
                setUsageSummary(data.data);
            }
            if (historyRes.ok) {
                const data = await historyRes.json();
                setPurchaseHistory(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching token data:', error);
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        fetchTokenData();
    }, [fetchTokenData]);

    // Handle PayPal return from redirect
    useEffect(() => {
        const handlePayPalReturn = async () => {
            const paypalStatus = searchParams.get('paypal');
            const purchaseId = searchParams.get('purchaseId');
            const token_param = searchParams.get('token'); // PayPal adds this

            if (!paypalStatus) return;

            // Clear URL params
            setSearchParams({});

            if (paypalStatus === 'success' && token_param) {
                setProcessingPaypal(true);
                try {
                    const authToken = await getAccessTokenSilently();
                    // Capture the PayPal payment
                    const response = await fetch(`${API_URL}/paypal/orders/${token_param}/capture`, {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json', 
                            Authorization: `Bearer ${authToken}` 
                        }
                    });
                    const data = await response.json();
                    
                    if (data.success) {
                        setPaypalSuccess({
                            success: true,
                            message: 'Payment completed successfully!',
                            tokens: data.data?.purchase?.totalTokens
                        });
                        await fetchTokenData();
                    } else {
                        setPaypalSuccess({
                            success: false,
                            message: data.message || 'Payment capture failed'
                        });
                    }
                } catch (error) {
                    console.error('PayPal capture error:', error);
                    setPaypalSuccess({
                        success: false,
                        message: 'Failed to process payment. Please contact support.'
                    });
                } finally {
                    setProcessingPaypal(false);
                }
            } else if (paypalStatus === 'cancelled') {
                setPaypalSuccess({
                    success: false,
                    message: 'Payment was cancelled'
                });
            }
        };

        handlePayPalReturn();
    }, [searchParams, setSearchParams, getAccessTokenSilently, fetchTokenData]);

    const validatePromoCode = async (code) => {
        if (!code.trim()) {
            setPromoValidation(null);
            return;
        }
        try {
            setValidatingPromo(true);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_URL}/tokens/promo/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ code, packageId: purchaseDialog.package?._id })
            });
            const data = await response.json();
            setPromoValidation(data);
        } catch (error) {
            setPromoValidation({ success: false, message: 'Error validating promo code' });
        } finally {
            setValidatingPromo(false);
        }
    };

    const handlePromoChange = (e) => {
        const code = e.target.value.toUpperCase();
        setPromoCode(code);
        if (code.length >= 3) validatePromoCode(code);
        else setPromoValidation(null);
    };

    const openPurchaseDialog = (pkg) => {
        setPurchaseDialog({ open: true, package: pkg });
        setPromoCode('');
        setPromoValidation(null);
        setPurchaseResult(null);
    };

    // Handle promo code purchase (100% discount)
    const handlePromoPurchase = async () => {
        if (!purchaseDialog.package) return;
        try {
            setPurchasing(true);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_URL}/tokens/purchase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ packageId: purchaseDialog.package._id, promoCode: promoCode || undefined })
            });
            const data = await response.json();
            setPurchaseResult(data);
            if (data.success) await fetchTokenData();
        } catch (error) {
            setPurchaseResult({ success: false, message: 'Purchase failed. Please try again.' });
        } finally {
            setPurchasing(false);
        }
    };

    // Handle PayPal purchase
    const handlePayPalPurchase = async () => {
        if (!purchaseDialog.package) return;
        try {
            setPurchasing(true);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_URL}/paypal/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ 
                    packageId: purchaseDialog.package._id, 
                    promoCode: promoCode || undefined 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                if (data.data.paymentRequired && data.data.approvalUrl) {
                    // Redirect to PayPal
                    window.location.href = data.data.approvalUrl;
                } else {
                    // Promo code covered full amount
                    setPurchaseResult(data);
                    await fetchTokenData();
                }
            } else {
                setPurchaseResult({ success: false, message: data.message || 'Failed to create PayPal order' });
            }
        } catch (error) {
            setPurchaseResult({ success: false, message: 'Failed to initiate PayPal payment. Please try again.' });
        } finally {
            setPurchasing(false);
        }
    };

    // Main purchase handler - uses PayPal for paid purchases, direct for promo codes
    const handlePurchase = async () => {
        const finalPrice = calculateFinalPrice();
        if (finalPrice <= 0) {
            // Free with promo code
            await handlePromoPurchase();
        } else {
            // Needs payment via PayPal
            await handlePayPalPurchase();
        }
    };

    const calculateFinalPrice = () => {
        if (!purchaseDialog.package) return 0;
        const originalPrice = purchaseDialog.package.price;
        if (promoValidation?.success && promoValidation.data) {
            if (promoValidation.data.discountType === 'percentage') {
                return originalPrice * (1 - promoValidation.data.discountValue / 100);
            }
            return Math.max(0, originalPrice - promoValidation.data.discountAmount);
        }
        return originalPrice;
    };

    // Debug: Adjust dashboard budget for testing
    const handleDebugAdjust = async (type) => {
        try {
            setDebugLoading(true);
            setDebugMessage(null);
            const token = await getAccessTokenSilently();
            
            let body = {};
            if (type === 'custom') {
                const value = parseFloat(debugValue);
                if (isNaN(value)) {
                    setDebugMessage({ success: false, message: 'Enter a valid number' });
                    return;
                }
                body = { dashboardAvailableUSD: value };
            } else if (type === '30%') {
                body = { dashboardPercentage: 30, resetNotifications: true };
            } else if (type === '10%') {
                body = { dashboardPercentage: 10, resetNotifications: true };
            } else if (type === '0%') {
                body = { dashboardAvailableUSD: 0, resetNotifications: true };
            } else if (type === '100%') {
                body = { dashboardPercentage: 100, resetNotifications: true };
            }
            
            const response = await fetch(`${API_URL}/tokens/debug/adjust-budget`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            
            setDebugMessage(data);
            if (data.success) {
                await fetchTokenData();
            }
        } catch (error) {
            setDebugMessage({ success: false, message: 'Failed to adjust budget' });
        } finally {
            setDebugLoading(false);
        }
    };

    // Migrate existing balance to new split budget system
    const handleMigrateBalance = async () => {
        try {
            setDebugLoading(true);
            setDebugMessage(null);
            const token = await getAccessTokenSilently();
            
            const response = await fetch(`${API_URL}/tokens/migrate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            
            setDebugMessage(data);
            if (data.success) {
                await fetchTokenData();
            }
        } catch (error) {
            setDebugMessage({ success: false, message: 'Failed to migrate balance' });
        } finally {
            setDebugLoading(false);
        }
    };

    const getBalanceStatus = () => {
        if (!balance?.balance) return { color: '#6b7280', text: 'No Subscription', bgColor: 'rgba(107, 114, 128, 0.2)' };
        if (balance.balance.isExpired) return { color: '#ef4444', text: 'Expired', bgColor: 'rgba(239, 68, 68, 0.2)' };
        if (!balance.balance.isSubscriptionActive) return { color: '#6b7280', text: 'Inactive', bgColor: 'rgba(107, 114, 128, 0.2)' };
        
        // Check dashboard budget percentage
        const pct = balance.balance.dashboardRemainingPercentage || 0;
        if (pct <= 0) return { color: '#ef4444', text: 'Dashboard Blocked', bgColor: 'rgba(239, 68, 68, 0.2)' };
        if (pct <= 10) return { color: '#f97316', text: 'Critical (10%)', bgColor: 'rgba(249, 115, 22, 0.2)' };
        if (pct <= 30) return { color: '#f59e0b', text: 'Low (30%)', bgColor: 'rgba(245, 158, 11, 0.2)' };
        return { color: '#22c55e', text: 'Good', bgColor: 'rgba(34, 197, 94, 0.2)' };
    };

    const balanceStatus = getBalanceStatus();
    const usagePercentage = balance?.balance ? 100 - (balance.balance.usagePercentage || 0) : 0;

    if (loading) {
        return (
            <div style={styles.pageWrapper}>
                <div style={styles.loading}>Loading token settings...</div>
            </div>
        );
    }

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.container}>
                {/* PayPal Processing Overlay */}
                {processingPaypal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <FaSync style={{ fontSize: '48px', color: '#0070ba', animation: 'spin 1s linear infinite' }} />
                        <div style={{ color: 'white', fontSize: '18px' }}>Processing your payment...</div>
                    </div>
                )}

                {/* PayPal Success/Error Banner */}
                {paypalSuccess && (
                    <div style={{
                        ...styles.alert,
                        ...(paypalSuccess.success ? styles.alertSuccess : styles.alertError),
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {paypalSuccess.success ? <FaCheck /> : <FaExclamationTriangle />}
                            <span>
                                {paypalSuccess.message}
                                {paypalSuccess.tokens && ` ${paypalSuccess.tokens.toLocaleString()} tokens added to your account.`}
                            </span>
                        </div>
                        <button 
                            onClick={() => setPaypalSuccess(null)} 
                            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '18px' }}
                        >
                            <FaTimes />
                        </button>
                    </div>
                )}

                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <FaCoins style={styles.headerIcon} />
                        <div>
                            <h1 style={styles.title}>Token Management</h1>
                            <p style={styles.subtitle}>Manage your AI tokens and view usage analytics</p>
                        </div>
                    </div>
                    <button style={styles.refreshBtn} onClick={fetchTokenData}>
                        <FaSync />
                    </button>
                </div>

                {/* Balance Card - Split Budget View */}
                <div style={styles.balanceCard}>
                    <div style={styles.balanceGrid}>
                        <div style={styles.balanceMain}>
                            {/* Widget Status Section */}
                            <div style={{ 
                                marginBottom: '20px',
                                padding: '12px',
                                backgroundColor: balance?.balance?.canUseWidget ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                borderRadius: '10px',
                                border: `1px solid ${balance?.balance?.canUseWidget ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between' 
                                }}>
                                    <div>
                                        <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                                            🔓 Widget
                                        </div>
                                        <div style={{ 
                                            fontSize: '28px', 
                                            fontWeight: '700', 
                                            color: balance?.balance?.canUseWidget ? '#22c55e' : '#ef4444' 
                                        }}>
                                            {balance?.balance?.canUseWidget ? 'UNLIMITED' : 'INACTIVE'}
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        backgroundColor: balance?.balance?.canUseWidget ? '#22c55e' : '#ef4444',
                                        color: 'white',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}>
                                        {balance?.balance?.canUseWidget ? '✓ ACTIVE' : '✗ BLOCKED'}
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
                                    Widget for new leads - never stops while subscription active
                                </div>
                            </div>
                            
                            {/* Dashboard Budget Section */}
                            <div style={{ ...styles.balanceLabel, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                📊 Dashboard Budget
                                {balance?.balance?.isDashboardExhausted && (
                                    <span style={{ 
                                        padding: '2px 8px', 
                                        borderRadius: '10px', 
                                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                        color: '#ef4444',
                                        fontSize: '10px'
                                    }}>
                                        BLOCKED
                                    </span>
                                )}
                            </div>
                            <div style={styles.balanceValue}>
                                ${balance?.balance?.dashboardAvailableUSD?.toFixed(2) || '0.00'}
                                <span style={{ fontSize: '18px', fontWeight: '400', marginLeft: '8px', opacity: 0.6 }}>
                                    remaining
                                </span>
                            </div>
                            <div style={styles.progressBar}>
                                <div style={{ 
                                    ...styles.progressFill, 
                                    width: `${balance?.balance?.dashboardRemainingPercentage || 0}%`,
                                    backgroundColor: balance?.balance?.dashboardAvailableUSD <= 0 ? '#ef4444' 
                                        : balance?.balance?.dashboardRemainingPercentage <= 10 ? '#f97316'
                                        : balance?.balance?.dashboardRemainingPercentage <= 30 ? '#f59e0b'
                                        : 'white'
                                }} />
                            </div>
                            <div style={{ fontSize: '13px', opacity: 0.8 }}>
                                ${balance?.balance?.dashboardUsedUSD?.toFixed(2) || '0.00'} used of ${balance?.balance?.dashboardBudgetUSD?.toFixed(2) || '0.00'} total
                                <span style={{ marginLeft: '8px', color: balance?.balance?.dashboardRemainingPercentage <= 30 ? '#f59e0b' : 'inherit' }}>
                                    ({balance?.balance?.dashboardRemainingPercentage || 0}% left)
                                </span>
                            </div>
                            
                            {/* Status badges */}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                                <div style={{ 
                                    ...styles.statusBadge, 
                                    backgroundColor: balance?.balance?.canUseDashboard ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)', 
                                    color: balance?.balance?.canUseDashboard ? '#22c55e' : '#ef4444',
                                    margin: 0
                                }}>
                                    {balance?.balance?.canUseDashboard ? <FaCheck /> : <FaExclamationTriangle />}
                                    Dashboard: {balance?.balance?.canUseDashboard ? 'Active' : 'Blocked'}
                                </div>
                                
                                {/* Auto-renewal status */}
                                {balance?.balance?.autoRenewalEnabled && (
                                    <div style={{ 
                                        ...styles.statusBadge, 
                                        backgroundColor: 'rgba(100, 116, 139, 0.2)', 
                                        color: '#94A3B8',
                                        margin: 0
                                    }}>
                                        <FaSync style={{ fontSize: '10px' }} /> Auto-renewal
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={styles.balanceStats}>
                            <div style={styles.statBox}>
                                <div style={styles.statValue}>
                                    {balance?.balance?.availableTokens?.toLocaleString() || 0}
                                </div>
                                <div style={styles.statLabel}>Dashboard Tokens</div>
                            </div>
                            <div style={styles.statBox}>
                                <div style={styles.statValue}>{balance?.balance?.daysUntilExpiry || '--'}</div>
                                <div style={styles.statLabel}>Days Until Renewal</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DEBUG: Budget Adjustment Panel - Show when subscription active OR when there's data that needs migration */}
                {(balance?.balance?.isSubscriptionActive || balance?.balance?.availableTokens > 0 || balance?.balance?.daysUntilExpiry > 0) && (
                    <div style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px'
                    }}>
                        {/* Migration notice - show if subscription isn't active but user has tokens/days */}
                        {!balance?.balance?.isSubscriptionActive && (balance?.balance?.availableTokens > 0 || balance?.balance?.daysUntilExpiry > 0) && (
                            <div style={{ 
                                backgroundColor: 'rgba(59, 130, 246, 0.2)', 
                                border: '2px solid rgba(59, 130, 246, 0.5)',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
                                    ⚠️ Migration Required
                                </div>
                                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '12px' }}>
                                    Your account uses the old token system. Click below to migrate to the new split budget system 
                                    (Widget: Unlimited + Dashboard: Token-based).
                                </p>
                                <button 
                                    onClick={handleMigrateBalance}
                                    disabled={debugLoading}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {debugLoading ? '⏳ Migrating...' : '🔄 Migrate to New Budget System'}
                                </button>
                                {debugMessage && (
                                    <div style={{ 
                                        marginTop: '12px', 
                                        padding: '8px 12px', 
                                        borderRadius: '6px',
                                        backgroundColor: debugMessage.success ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                        color: debugMessage.success ? '#22c55e' : '#ef4444',
                                        fontSize: '13px'
                                    }}>
                                        {debugMessage.message}
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <span style={{ color: '#ef4444', fontWeight: '600' }}>🔧 DEBUG: Test Budget Levels</span>
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                                (Adjust dashboard budget to test notifications & blocking)
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <button 
                                onClick={() => handleDebugAdjust('100%')}
                                disabled={debugLoading}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(34, 197, 94, 0.4)',
                                    backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                    color: '#22c55e',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                Reset to 100%
                            </button>
                            <button 
                                onClick={() => handleDebugAdjust('30%')}
                                disabled={debugLoading}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                    backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                    color: '#f59e0b',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                Set to 30% (Warning)
                            </button>
                            <button 
                                onClick={() => handleDebugAdjust('10%')}
                                disabled={debugLoading}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(249, 115, 22, 0.4)',
                                    backgroundColor: 'rgba(249, 115, 22, 0.2)',
                                    color: '#f97316',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                Set to 10% (Urgent)
                            </button>
                            <button 
                                onClick={() => handleDebugAdjust('0%')}
                                disabled={debugLoading}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                Set to 0% (Blocked)
                            </button>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                    type="number"
                                    placeholder="Custom $"
                                    value={debugValue}
                                    onChange={(e) => setDebugValue(e.target.value)}
                                    style={{
                                        width: '80px',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(100, 150, 200, 0.3)',
                                        backgroundColor: 'rgba(35, 40, 50, 0.9)',
                                        color: 'white',
                                        fontSize: '13px'
                                    }}
                                />
                                <button 
                                    onClick={() => handleDebugAdjust('custom')}
                                    disabled={debugLoading}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(100, 116, 139, 0.4)',
                                        backgroundColor: 'rgba(100, 116, 139, 0.2)',
                                        color: '#94A3B8',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    Set
                                </button>
                            </div>
                        </div>
                        
                        {debugMessage && (
                            <div style={{ 
                                marginTop: '12px', 
                                padding: '8px 12px', 
                                borderRadius: '6px',
                                backgroundColor: debugMessage.success ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: debugMessage.success ? '#22c55e' : '#ef4444',
                                fontSize: '13px'
                            }}>
                                {debugMessage.message}
                                {debugMessage.data && (
                                    <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                                        | Dashboard: ${debugMessage.data.dashboardAvailableUSD?.toFixed(2)} ({debugMessage.data.dashboardRemainingPercentage}%)
                                        | Widget: {debugMessage.data.canUseWidget ? '✅ Active' : '❌ Blocked'}
                                        | Dashboard: {debugMessage.data.canUseDashboard ? '✅ Active' : '❌ Blocked'}
                                    </span>
                                )}
                            </div>
                        )}
                        
                        <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            💡 At 0% dashboard: Widget continues to work (unlimited), Dashboard features blocked
                        </div>
                        
                        {/* Migration button - for existing accounts that need the new budget system */}
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <button 
                                    onClick={handleMigrateBalance}
                                    disabled={debugLoading}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(59, 130, 246, 0.4)',
                                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                        color: '#3b82f6',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '600'
                                    }}
                                >
                                    🔄 Migrate to New Budget System
                                </button>
                                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                                    Use this if Widget shows "INACTIVE" despite having an active subscription
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div style={styles.tabs}>
                    {[
                        { icon: FaShoppingCart, label: 'Purchase Tokens' },
                        { icon: FaChartBar, label: 'Usage Analytics' },
                        { icon: FaHistory, label: 'Purchase History' }
                    ].map((tab, idx) => (
                        <button
                            key={idx}
                            onClick={() => setActiveTab(idx)}
                            style={{ ...styles.tab, ...(activeTab === idx ? styles.tabActive : styles.tabInactive) }}
                        >
                            <tab.icon /> {tab.label}
                        </button>
                    ))}
                </div>

                {/* Purchase Tab */}
                {activeTab === 0 && (
                    <>
                        {/* Package Structure Explanation */}
                        <div style={{
                            backgroundColor: 'rgba(100, 116, 139, 0.15)',
                            border: '1px solid rgba(100, 116, 139, 0.4)',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '24px'
                        }}>
                            <h3 style={{ color: 'white', margin: '0 0 12px 0', fontSize: '16px' }}>📦 How Packages Work</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <div style={{ color: '#22c55e', fontWeight: '600', marginBottom: '4px' }}>🔓 Widget ($50/month)</div>
                                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                                        Unlimited widget usage for your website. Never blocked while subscription is active. 
                                        This is for your new leads - the most important feature!
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: '#64748B', fontWeight: '600', marginBottom: '4px' }}>📊 Dashboard (Remaining)</div>
                                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                                        All dashboard features, AI assistants, video calls, transcription. 
                                        Notifications at 30%, 10% remaining. Blocked at 0% (widget continues).
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div style={styles.promoBanner}>
                            <FaTag style={styles.promoIcon} />
                            <span style={styles.promoText}>
                                🎉 Special Launch Offer! Use promo code <strong>iMavenAi</strong> for 100% OFF on all packages!
                            </span>
                        </div>
                        <div style={styles.packagesGrid}>
                            {packages.map((pkg) => (
                                <div
                                    key={pkg._id}
                                    style={{ ...styles.packageCard, ...(pkg.isPopular ? styles.packageCardPopular : {}) }}
                                    onClick={() => openPurchaseDialog(pkg)}
                                >
                                    {pkg.isPopular && <div style={styles.popularBadge}>Most Popular</div>}
                                    <div style={styles.packageName}>{pkg.name}</div>
                                    <div style={styles.packageDescription}>{pkg.description}</div>
                                    <div style={styles.packagePrice}>${pkg.price}<span style={{ fontSize: '14px', fontWeight: '400' }}>/month</span></div>
                                    
                                    {/* Split budget display */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 1fr', 
                                        gap: '8px', 
                                        marginBottom: '16px',
                                        padding: '12px',
                                        backgroundColor: 'rgba(100, 116, 139, 0.1)',
                                        borderRadius: '8px'
                                    }}>
                                        <div>
                                            <div style={{ color: '#22c55e', fontSize: '13px', fontWeight: '600' }}>Widget</div>
                                            <div style={{ color: 'white', fontSize: '18px' }}>${pkg.widgetBudget || 50}</div>
                                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Unlimited</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#64748B', fontSize: '13px', fontWeight: '600' }}>Dashboard</div>
                                            <div style={{ color: 'white', fontSize: '18px' }}>${pkg.dashboardBudget || (pkg.price - 50)}</div>
                                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>~{((pkg.tokens || 0) / 1000000).toFixed(1)}M tokens</div>
                                        </div>
                                    </div>
                                    
                                    <ul style={styles.featureList}>
                                        {pkg.features?.map((feature, i) => (
                                            <li key={i} style={styles.featureItem}>
                                                <FaCheck style={styles.featureIcon} /> {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <button style={{ ...styles.selectBtn, ...(pkg.isPopular ? styles.selectBtnPrimary : styles.selectBtnSecondary) }}>
                                        Subscribe Now
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Usage Tab */}
                {activeTab === 1 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Usage by Task</h3>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Task Type</th>
                                        <th style={{ ...styles.th, textAlign: 'right' }}>Tokens Used</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageSummary?.byTask && Object.entries(usageSummary.byTask).map(([task, tokens]) => (
                                        <tr key={task}>
                                            <td style={styles.td}>{task.replace(/([A-Z])/g, ' $1').trim()}</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>{tokens.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Usage by Model</h3>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>AI Model</th>
                                        <th style={{ ...styles.th, textAlign: 'right' }}>Tokens Used</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageSummary?.byModel && Object.entries(usageSummary.byModel).map(([model, tokens]) => (
                                        <tr key={model}>
                                            <td style={styles.td}>{model}</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>{tokens.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
                            <h3 style={styles.cardTitle}>This Month Summary</h3>
                            <div style={{ display: 'flex', gap: '40px' }}>
                                <div>
                                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#94A3B8' }}>
                                        {usageSummary?.totalTokens?.toLocaleString() || 0}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Tokens Used</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#94A3B8' }}>
                                        ${usageSummary?.totalCost?.toFixed(2) || '0.00'}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Estimated Cost</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* History Tab */}
                {activeTab === 2 && (
                    <div style={styles.card}>
                        <h3 style={styles.cardTitle}>Purchase History</h3>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Package</th>
                                    <th style={styles.th}>Tokens</th>
                                    <th style={styles.th}>Promo Code</th>
                                    <th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchaseHistory.length === 0 ? (
                                    <tr><td colSpan={6} style={styles.emptyState}>No purchases yet</td></tr>
                                ) : (
                                    purchaseHistory.map((p) => (
                                        <tr key={p._id}>
                                            <td style={styles.td}>{new Date(p.purchasedAt).toLocaleDateString()}</td>
                                            <td style={styles.td}>{p.packageName}</td>
                                            <td style={styles.td}>{p.totalTokens?.toLocaleString()}</td>
                                            <td style={styles.td}>{p.promoCode || '-'}</td>
                                            <td style={styles.td}>${p.finalPrice?.toFixed(2)}</td>
                                            <td style={styles.td}>
                                                <span style={{ color: p.status === 'completed' ? '#22c55e' : '#f59e0b' }}>
                                                    {p.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Purchase Modal */}
                {purchaseDialog.open && (
                    <div style={styles.modal}>
                        <div style={styles.modalContent}>
                            <div style={styles.modalHeader}>
                                <h2 style={styles.modalTitle}>Purchase {purchaseDialog.package?.name}</h2>
                                <button style={styles.closeBtn} onClick={() => !purchasing && setPurchaseDialog({ open: false, package: null })}>
                                    <FaTimes />
                                </button>
                            </div>

                            {purchaseResult?.success ? (
                                <div style={{ ...styles.alert, ...styles.alertSuccess }}>
                                    🎉 Purchase Successful! {purchaseResult.data?.purchase?.totalTokens?.toLocaleString()} tokens added.
                                </div>
                            ) : (
                                <>
                                    <div style={styles.summaryBox}>
                                        <div style={styles.summaryRow}>
                                            <span style={{ ...styles.summaryLabel, color: '#22c55e' }}>🔓 Widget Budget</span>
                                            <span style={{ ...styles.summaryValue, color: '#22c55e' }}>
                                                ${purchaseDialog.package?.widgetBudget || 50} (Unlimited)
                                            </span>
                                        </div>
                                        <div style={styles.summaryRow}>
                                            <span style={styles.summaryLabel}>📊 Dashboard Budget</span>
                                            <span style={styles.summaryValue}>
                                                ${purchaseDialog.package?.dashboardBudget || ((purchaseDialog.package?.price || 0) - 50)}
                                            </span>
                                        </div>
                                        <div style={styles.summaryRow}>
                                            <span style={styles.summaryLabel}>Dashboard Tokens</span>
                                            <span style={styles.summaryValue}>
                                                ~{((purchaseDialog.package?.tokens || 0) / 1000000).toFixed(1)}M tokens
                                            </span>
                                        </div>
                                        <div style={styles.summaryRow}>
                                            <span style={styles.summaryLabel}>Subscription</span>
                                            <span style={styles.summaryValue}>Monthly (auto-renews)</span>
                                        </div>
                                    </div>
                                    
                                    <div style={{ 
                                        backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginBottom: '16px',
                                        fontSize: '12px',
                                        color: '#22c55e'
                                    }}>
                                        ✓ Widget never stops while subscription is active<br/>
                                        ✓ Dashboard notifies at 30%, 10% remaining<br/>
                                        ✓ Auto-renewal keeps your service running
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Enter promo code (e.g., iMavenAi)"
                                        value={promoCode}
                                        onChange={handlePromoChange}
                                        style={styles.input}
                                    />

                                    {promoValidation && (
                                        <div style={{ ...styles.alert, ...(promoValidation.success ? styles.alertSuccess : styles.alertError) }}>
                                            {promoValidation.success ? `${promoValidation.data?.discountValue}% discount applied!` : promoValidation.message}
                                        </div>
                                    )}

                                    {purchaseResult && !purchaseResult.success && (
                                        <div style={{ ...styles.alert, ...styles.alertError }}>{purchaseResult.message}</div>
                                    )}

                                    <div style={styles.summaryBox}>
                                        <div style={styles.summaryRow}>
                                            <span style={styles.summaryLabel}>Original Price</span>
                                            <span style={styles.summaryValue}>${purchaseDialog.package?.price?.toFixed(2)}</span>
                                        </div>
                                        {promoValidation?.success && (
                                            <div style={styles.summaryRow}>
                                                <span style={{ color: '#22c55e' }}>Discount</span>
                                                <span style={{ color: '#22c55e' }}>
                                                    -${(purchaseDialog.package?.price - calculateFinalPrice()).toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        <div style={styles.totalRow}>
                                            <span>Total</span>
                                            <span style={{ color: '#22c55e' }}>${calculateFinalPrice().toFixed(2)}</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={handlePurchase}
                                        disabled={purchasing}
                                        style={{ 
                                            ...styles.selectBtn, 
                                            ...styles.selectBtnPrimary, 
                                            opacity: purchasing ? 0.6 : 1,
                                            background: calculateFinalPrice() > 0 
                                                ? 'linear-gradient(135deg, #0070ba 0%, #003087 100%)' 
                                                : undefined,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {purchasing ? (
                                            <>
                                                <FaSync style={{ animation: 'spin 1s linear infinite' }} />
                                                Processing...
                                            </>
                                        ) : calculateFinalPrice() > 0 ? (
                                            <>
                                                <FaPaypal />
                                                Pay ${calculateFinalPrice().toFixed(2)} with PayPal
                                            </>
                                        ) : (
                                            <>
                                                <FaCheck />
                                                Activate Free Package
                                            </>
                                        )}
                                    </button>
                                </>
                            )}

                            {purchaseResult?.success && (
                                <button
                                    onClick={() => setPurchaseDialog({ open: false, package: null })}
                                    style={{ ...styles.selectBtn, ...styles.selectBtnPrimary, marginTop: '16px' }}
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenSettings;
